export interface RoadmapMilestone {
  phase: string
  title: string
  description: string
  timeframe: string
  actions: string[]   // 2–3 concrete checkable steps
}

export interface UniversityRecommendation {
  name: string
  country: string
  relevantFor: string[]
  examBodies: string[]
  programmes: string[]
  cutoffGuide?: string
  link?: string
}

export interface CareerRoadmap {
  broadPathId: string
  milestones: RoadmapMilestone[]
  universities: UniversityRecommendation[]
}

export const CAREER_ROADMAPS: CareerRoadmap[] = [
  // ─── STEM & Technology ────────────────────────────────────────────────────────
  {
    broadPathId: "broad_stem_tech",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Build your maths & science base",
        description: "Focus on Mathematics, Basic Science, and Basic Technology. Participate in science clubs, coding clubs, or robotics if available.",
        actions: [
          "Join your school's science or computer club this term",
          "Try 10 Khan Academy maths challenges or explore code.org/Scratch",
          "Visit a science or technology exhibition in your city",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Choose Science stream & deepen skills",
        description: "Take Further Mathematics, Physics, Chemistry or Computer Science. Enter science or tech competitions. Start exploring free online coding platforms.",
        actions: [
          "Register on freeCodeCamp, Sololearn, or Programiz and complete one module",
          "Enter at least one school or state science/tech competition this year",
          "Solve 3 WAEC past questions in Maths and Physics every weekend",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "Ace your core science subjects",
        description: "Target strong grades in Mathematics, Physics, Chemistry/Computer Science. Aim for B2 or above. Register for UTME with the right subject combination.",
        actions: [
          "Complete 5 full past WAEC papers for Mathematics and Physics",
          "Confirm your JAMB subject combination for your target programme (e.g. Maths, Physics, Chemistry for Engineering)",
          "Register for JAMB UTME before the deadline — aim for 220+",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "UTME / Direct Entry / Scholarship",
        description: "Score 200+ in UTME for competitive programmes. Explore scholarships (MTN Foundation, PTDF, Dangote). Apply to JAMB-listed universities or pursue Direct Entry with A-Levels/ND.",
        actions: [
          "Apply to at least 4 universities including one safety school",
          "Search and apply for 2+ scholarships: MTN Foundation, PTDF, Dangote Foundation",
          "Take a free Python or web development course while awaiting admission",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–4/5",
        title: "Pursue your degree & build portfolio",
        description: "Enrol in Engineering, Computer Science, Mathematics, or related field. Do internships from Year 2. Build GitHub portfolio. Enter hackathons. Network via IEEE Student Branch.",
        actions: [
          "Create a GitHub profile and push 3 projects by end of Year 2",
          "Apply for at least one internship every long vacation from Year 2",
          "Join the IEEE Student Branch or Google Developer Student Club at your university",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "Certifications & first job",
        description: "Get industry certifications (AWS, Google Cloud, CISCO). Apply to NYSC with a tech/engineering company. Target tech hubs: Lagos, Abuja, or remote work.",
        actions: [
          "Earn at least one cloud certification: AWS Cloud Practitioner, Google Associate, or Azure Fundamentals",
          "Apply for NYSC posting at a top tech company or startup in Lagos/Abuja",
          "Build a strong LinkedIn profile and connect with 50+ professionals in your field",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos (UNILAG)", country: "Nigeria", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "NECO"], programmes: ["Computer Science", "Electrical Engineering", "Systems Engineering"], cutoffGuide: "220+ UTME" },
      { name: "University of Ibadan (UI)", country: "Nigeria", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "NECO"], programmes: ["Computer Science", "Mathematics", "Physics"], cutoffGuide: "210+ UTME" },
      { name: "Covenant University", country: "Nigeria", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "NECO"], programmes: ["Computer Science", "Electrical & Electronics Engineering", "Information Technology"], cutoffGuide: "220+ UTME" },
      { name: "Obafemi Awolowo University (OAU)", country: "Nigeria", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "NECO"], programmes: ["Computer Science", "Electronic & Electrical Engineering"], cutoffGuide: "210+ UTME" },
      { name: "African University of Science & Technology (AUST)", country: "Nigeria", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "NECO", "IGCSE"], programmes: ["Computer Science", "Applied Mathematics"], cutoffGuide: "Postgraduate / Direct Entry" },
      { name: "University of Ghana", country: "Ghana", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "WASSCE"], programmes: ["Computer Science", "Electrical Engineering", "Mathematics"], cutoffGuide: "Aggregate 6–12 WASSCE" },
      { name: "Ashesi University", country: "Ghana", relevantFor: ["broad_stem_tech"], examBodies: ["WAEC", "IGCSE", "IB"], programmes: ["Computer Science", "Engineering", "Management Information Systems"] },
    ],
  },

  // ─── Health & Medicine ────────────────────────────────────────────────────────
  {
    broadPathId: "broad_health_medicine",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Strengthen Biology & Chemistry",
        description: "Focus on Basic Science and Mathematics. Visit hospitals for exposure. Join the Red Cross or first aid club if available at your school.",
        actions: [
          "Join the Red Cross, Health Prefect, or first aid club at school",
          "Watch a documentary on healthcare or a hospital process (YouTube: 'How surgery works')",
          "Shadow a nurse or pharmacist at a nearby clinic for even one afternoon",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Science stream: Biology, Chemistry, Physics",
        description: "All three are required for Medicine. Aim for high grades. Volunteer at health camps or community health events. Read about the healthcare system.",
        actions: [
          "Aim for nothing below B2 in Biology, Chemistry, and Physics this term",
          "Volunteer at a community health camp or school health day",
          "Read about one medical specialty per month — search 'What does a cardiologist do?'",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "Strong passes in Biology, Chemistry, Physics",
        description: "Target A1/B2 in Biology and Chemistry. Register UTME with Biology, Chemistry, Physics for Medicine or Biology, Chemistry, Maths for health sciences.",
        actions: [
          "Complete 5 past WAEC papers for Biology and Chemistry",
          "Register JAMB: Biology, Chemistry, Physics (Medicine) or Biology, Chemistry, Maths (Pharmacy/Nursing)",
          "Research Post-UTME screening styles for your top 3 medical schools — they vary significantly",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "UTME + Post-UTME screening",
        description: "Score 220+ UTME for Medicine. Post-UTME CBT screening is highly competitive. Have a backup plan: Pharmacy, Nursing, Medical Lab Science.",
        actions: [
          "Prepare specifically for Post-UTME CBT — buy school-specific past question booklets",
          "Identify 2 backup programmes: Pharmacy, Nursing, or Medical Lab Science",
          "Research medical scholarships: UNILAG, ABU, and state government medical bursaries",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–6",
        title: "Medical or health sciences degree",
        description: "MBBS/MBChB is 6 years. Maintain your GPA. Do clinical rotations and electives. Join student medical associations. Consider research opportunities.",
        actions: [
          "Join the Medical Students Society of Nigeria (MSN) or Pharmacy Students Association in Year 1",
          "Volunteer in community health outreaches or hospital teaching units every semester",
          "Maintain a GPA of 3.5+ — it affects residency placement significantly",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "Housemanship, NYSC, specialisation",
        description: "Complete one year housemanship. NYSC in a health facility. Begin residency applications for specialisation. Consider postgraduate study abroad.",
        actions: [
          "Apply for housemanship at a Federal Teaching Hospital for the broadest clinical exposure",
          "Research residency programmes and begin applications at least 6 months before completion",
          "Join the Nigerian Medical Association (NMA) and attend at least one annual conference",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos — College of Medicine", country: "Nigeria", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "NECO"], programmes: ["Medicine & Surgery", "Pharmacy", "Medical Laboratory Science"], cutoffGuide: "230+ UTME" },
      { name: "Ahmadu Bello University (ABU)", country: "Nigeria", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "NECO"], programmes: ["Medicine & Surgery", "Veterinary Medicine", "Pharmacy"], cutoffGuide: "220+ UTME" },
      { name: "University of Nigeria, Nsukka (UNN)", country: "Nigeria", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "NECO"], programmes: ["Medicine", "Pharmacy", "Nursing", "Medical Lab Science"], cutoffGuide: "220+ UTME" },
      { name: "Babcock University", country: "Nigeria", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "NECO"], programmes: ["Medicine & Surgery", "Nursing", "Medical Laboratory Science"] },
      { name: "University of Ghana Medical School", country: "Ghana", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "WASSCE"], programmes: ["Medicine & Surgery", "Pharmacy"] },
      { name: "KNUST School of Medical Sciences", country: "Ghana", relevantFor: ["broad_health_medicine"], examBodies: ["WAEC", "WASSCE"], programmes: ["Human Biology", "Pharmacy", "Medical Laboratory Technology"] },
    ],
  },

  // ─── Business & Finance ───────────────────────────────────────────────────────
  {
    broadPathId: "broad_business_finance",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Build numeracy & entrepreneurship awareness",
        description: "Focus on Mathematics and Social Studies. Join Junior Achievement Nigeria if available. Start a small side project or school business.",
        actions: [
          "Try running a small school business — selling a product or service to classmates",
          "Read the business section of any Nigerian newspaper once a week",
          "Sign up for Junior Achievement Nigeria (JAN) if it operates in your area",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Commercial stream: Economics, Accounting, Commerce",
        description: "Choose the Commercial track. Study Financial Accounting, Economics, and Commerce/Business Studies. Follow business news. Enter the Young Investors competition.",
        actions: [
          "Study WAEC past questions for Financial Accounting and Economics weekly",
          "Keep a personal budget for 30 days — track every income and expense",
          "Follow a Nigerian business podcast or YouTube channel (e.g. Business Insider Africa)",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "Strong grades in Economics & Maths",
        description: "Aim for A1 in Financial Accounting and Economics. Target UTME subjects: Economics, Mathematics, and one other depending on your programme.",
        actions: [
          "Complete 5 past WAEC papers for Financial Accounting and Economics",
          "Confirm JAMB subject combo: Economics, Maths + one of Commerce/Government/Geography",
          "Research ICAN and ACCA — understand the qualification structure before university",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "UTME / Scholarship applications",
        description: "Score 180–200+ for Business/Accounting programmes. Apply for merit scholarships. Consider HND route for Accountancy.",
        actions: [
          "Apply to at least 4 universities — include Covenant, UNILAG, UI, and OAU",
          "Apply for scholarships: UBA Foundation, Access Bank, Zenith Bank Education Fund",
          "Read Investopedia daily for 10 minutes to build your financial vocabulary",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–4",
        title: "Business degree + professional certifications",
        description: "Study Accounting, Finance, Economics, or Business Administration. Begin ICAN or ACCA registration from Year 2. Intern at banks, audit firms, or fintechs.",
        actions: [
          "Register as an ICAN student member from Year 2 and start the Foundation level",
          "Apply for internships at banks, fintech companies (Flutterwave, Paystack), or Big 4 firms every vacation",
          "Join the Finance & Investment Club or Junior Chamber International (JCI) on campus",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "NYSC, professional exams, first job",
        description: "Complete ICAN/ACCA exams alongside NYSC. Target Big 4 accounting firms or banks. Fintech is a strong option — Flutterwave, Paystack, PiggyVest hire graduates.",
        actions: [
          "Sit ICAN Professional Exam Part 1 while completing NYSC",
          "Apply to Big 4 firms (KPMG, PwC, EY, Deloitte) and leading fintechs simultaneously",
          "Build a finance LinkedIn profile — post about market observations and what you're learning",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos", country: "Nigeria", relevantFor: ["broad_business_finance"], examBodies: ["WAEC", "NECO"], programmes: ["Accounting", "Banking & Finance", "Business Administration", "Economics"], cutoffGuide: "200+ UTME" },
      { name: "Covenant University", country: "Nigeria", relevantFor: ["broad_business_finance"], examBodies: ["WAEC", "NECO"], programmes: ["Accounting", "Finance", "Business Management"] },
      { name: "Lagos Business School (LBS)", country: "Nigeria", relevantFor: ["broad_business_finance"], examBodies: ["all"], programmes: ["MBA", "Executive Education"], cutoffGuide: "Postgraduate" },
      { name: "Pan-Atlantic University", country: "Nigeria", relevantFor: ["broad_business_finance"], examBodies: ["WAEC", "NECO"], programmes: ["Economics", "Business Administration"] },
      { name: "University of Ghana Business School", country: "Ghana", relevantFor: ["broad_business_finance"], examBodies: ["WAEC", "WASSCE"], programmes: ["Accounting", "Finance", "Business Administration"] },
      { name: "KNUST School of Business", country: "Ghana", relevantFor: ["broad_business_finance"], examBodies: ["WAEC", "WASSCE"], programmes: ["Accounting", "Finance", "Economics"] },
    ],
  },

  // ─── Creative Arts ────────────────────────────────────────────────────────────
  {
    broadPathId: "broad_creative_arts",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Explore your creative medium",
        description: "Identify your area: visual art, music, drama, design, film, or writing. Join the school art club, drama society, or choir. Start producing work.",
        actions: [
          "Create 10 pieces in your chosen medium this term — sketches, writings, recordings, or photos",
          "Join the school art club, drama society, school band, or literary club",
          "Start a simple portfolio folder — physical or a Google Drive — to collect your best work",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Arts stream or combine with Sciences",
        description: "Take Fine Art, Music, Literature-in-English, or Creative Arts as elective subjects. Build a portfolio. Enter school exhibitions or competitions.",
        actions: [
          "Enter at least one school or inter-school creative competition this year",
          "Open an Instagram, Behance, or SoundCloud account and post your best work publicly",
          "Identify 3 working Nigerian creatives in your field and study how they built their careers",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "Portfolio + examination passes",
        description: "For visual arts, prepare a portfolio alongside your WAEC. For performing arts and design schools, entrance auditions or portfolio reviews matter as much as exam grades.",
        actions: [
          "Prepare a portfolio of 10–15 strong pieces for art or design school applications",
          "Research specific entrance requirements (audition, portfolio review, interview) for your top schools",
          "Take at least one free online short course: Canva Design, Coursera Creative Writing, or YouTube Music Production",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "Art school, polytechnic, or degree programme",
        description: "Apply to universities with strong creative programmes. Polytechnic Mass Communication or Fine & Applied Arts programmes are competitive alternatives.",
        actions: [
          "Apply to at least 3 programmes: one university, one polytechnic, and one online option as backup",
          "Submit your portfolio for review at your top 2 creative schools if required",
          "Freelance for small clients (logo design, photography, music gigs) while waiting for admission",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–4",
        title: "Degree + real work experience",
        description: "Study Fine Art, Graphic Design, Mass Communication, Architecture, or Music. Freelance alongside your degree. Build an Instagram/Behance portfolio.",
        actions: [
          "Land at least 3 paid freelance clients by end of Year 2 — Fiverr, Upwork, or local referrals",
          "Maintain a public portfolio on Behance (design), IMDB (film), or SoundCloud (music) and update it every semester",
          "Intern at an agency, production company, publisher, or studio each long vacation",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "Freelance, studio, or agency work",
        description: "Build your client base during NYSC. The creative economy in Nigeria is booming — Nollywood, Afrobeats, ad agencies, and tech companies all need creatives.",
        actions: [
          "Secure at least 5 recurring clients or a retainer contract by the end of NYSC",
          "Register with the Nigerian Graphic Design Guild, NUJ, or relevant professional body in your field",
          "Pitch your portfolio to 3 agencies, studios, or brands actively — track responses",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos — Faculty of Arts", country: "Nigeria", relevantFor: ["broad_creative_arts"], examBodies: ["WAEC", "NECO"], programmes: ["Fine & Applied Arts", "Music", "Creative Arts", "Mass Communication"] },
      { name: "Obafemi Awolowo University", country: "Nigeria", relevantFor: ["broad_creative_arts"], examBodies: ["WAEC", "NECO"], programmes: ["Fine & Applied Arts", "Architecture", "Dramatic Arts"] },
      { name: "Nigerian Institute of Journalism", country: "Nigeria", relevantFor: ["broad_creative_arts"], examBodies: ["WAEC", "NECO"], programmes: ["Journalism", "Mass Communication", "Public Relations"] },
      { name: "University of Ghana — School of Arts", country: "Ghana", relevantFor: ["broad_creative_arts"], examBodies: ["WAEC", "WASSCE"], programmes: ["Fine Art", "Theatre Studies", "Music", "Communication Studies"] },
      { name: "KNUST — College of Art & Built Environment", country: "Ghana", relevantFor: ["broad_creative_arts"], examBodies: ["WAEC", "WASSCE"], programmes: ["Architecture", "Industrial Art", "Communication Design"] },
    ],
  },

  // ─── Law & Social Sciences ────────────────────────────────────────────────────
  {
    broadPathId: "broad_law_social",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Develop critical thinking & communication",
        description: "Focus on English Language and Social Studies. Join the debate club or Model UN. Read newspapers — understanding current affairs is essential for Law.",
        actions: [
          "Join the school debate club or Model United Nations (MUN) club",
          "Read one newspaper editorial a week and write a 3-sentence response to it",
          "Research how a Nigerian court case works — YouTube 'How Nigerian courts work'",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Arts or Social Science stream",
        description: "Take Government, Literature-in-English, Economics, and CRS/IRS. English Language A1 is non-negotiable for Law. Write for the school magazine.",
        actions: [
          "Target nothing below B2 in English Language every term — it is mandatory for Law",
          "Write one editorial or opinion piece for the school magazine or student union this term",
          "Track current affairs weekly: watch Channels TV or read The Punch / Vanguard",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "A1 in English + strong Government/Lit",
        description: "Target A1 in English Language — required for Law at virtually all Nigerian universities. UTME subjects for Law: English, Government, Literature or CRK.",
        actions: [
          "Complete 5 past WAEC papers for English Language — grammar and comprehension sections",
          "Confirm JAMB subjects: English, Government, Literature-in-English (or CRK — varies by school)",
          "Research the Nigerian Law School process — it's a mandatory year after your LLB degree",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "UTME + post-UTME for Law / Social Sciences",
        description: "Law is competitive: 220+ UTME at top schools. For Social Sciences, 180–200+ is typical. The Nigerian Law School comes after your LLB — plan for 5 years total.",
        actions: [
          "Score 220+ UTME for top Law schools (UNILAG, UI, OAU, ABU)",
          "Buy past Post-UTME question booklets specific to your top 2 school choices",
          "Identify 2 backup programmes: Political Science, Sociology, or International Relations",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–5 (Law) / 1–4 (Social Sciences)",
        title: "Degree + practical experience",
        description: "LLB is 5 years + 1 year Nigerian Law School. Join Law Clinic. For Social Sciences — intern at NGOs, government agencies, or media houses.",
        actions: [
          "Join the Faculty of Law moot court team or mooting competitions from Year 2",
          "Intern at a law firm, NGO, government ministry, or media house every vacation",
          "Complete one online certification in Human Rights, Public Policy, or International Law",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "Bar exams, NYSC, first practice",
        description: "Complete the Nigerian Law School Bar Part II exams. NYSC in a law firm or government ministry. For Social Sciences — policy research, journalism, NGO, consulting.",
        actions: [
          "Sit the Nigerian Law School Bar Part II examination and prepare 6 months in advance",
          "Secure NYSC posting at a law firm or government agency for maximum professional exposure",
          "Attend one NBA (Nigerian Bar Association) event or legal conference during NYSC",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos — Faculty of Law", country: "Nigeria", relevantFor: ["broad_law_social"], examBodies: ["WAEC", "NECO"], programmes: ["Law (LLB)", "Political Science", "Sociology", "International Relations"], cutoffGuide: "220+ UTME for Law" },
      { name: "University of Ibadan", country: "Nigeria", relevantFor: ["broad_law_social"], examBodies: ["WAEC", "NECO"], programmes: ["Law", "Political Science", "Sociology", "Economics"] },
      { name: "Obafemi Awolowo University", country: "Nigeria", relevantFor: ["broad_law_social"], examBodies: ["WAEC", "NECO"], programmes: ["Law", "International Relations", "Political Science"] },
      { name: "Nigerian Institute of International Affairs (NIIA)", country: "Nigeria", relevantFor: ["broad_law_social"], examBodies: ["all"], programmes: ["International Relations", "Diplomacy"], cutoffGuide: "Postgraduate" },
      { name: "University of Ghana — School of Law", country: "Ghana", relevantFor: ["broad_law_social"], examBodies: ["WAEC", "WASSCE"], programmes: ["Law (LLB)", "Political Science", "Sociology"] },
    ],
  },

  // ─── Law & Governance ─────────────────────────────────────────────────────────
  {
    broadPathId: "broad_law_governance",
    milestones: [
      {
        phase: "Secondary (Junior)", timeframe: "JSS1–JSS3",
        title: "Understand governance & civic responsibility",
        description: "Focus on Social Studies, Civic Education, and English Language. Learn how government works — local, state, and federal. Debate and public speaking clubs are valuable.",
        actions: [
          "Join the debate club or school student government (senior prefect body, class rep)",
          "Read about how Nigeria's three tiers of government work — Wikipedia or Civic Education textbook",
          "Attend any community meeting, town hall, or civic event with your parents if possible",
        ],
      },
      {
        phase: "Secondary (Senior)", timeframe: "SS1–SS2",
        title: "Government, Law, and Public Policy focus",
        description: "Take Government, Literature-in-English, and Economics or History. Follow politics and policy news. Research the roles of lawyers in governance and policy.",
        actions: [
          "Score consistent A grades in Government and English Language — both are critical for Law",
          "Study how major Nigerian laws are made — research the process from Bill to Act",
          "Write an essay on a policy issue that affects your community: education, healthcare, or security",
        ],
      },
      {
        phase: "WAEC / NECO Prep", timeframe: "SS3",
        title: "Secure A1 in English and Government",
        description: "Both are critical for Law or Political Science admission. UTME subjects vary — confirm with your target institution.",
        actions: [
          "Complete 5 past WAEC papers for Government and English Language",
          "Confirm JAMB subject requirements for Law or Political Science at your top 3 schools",
          "Apply for any pre-university summer programmes in Law or Public Policy if available",
        ],
      },
      {
        phase: "University Entry", timeframe: "After WAEC",
        title: "Law school or Political Science entry",
        description: "Law requires 220+ UTME at top schools. Political Science and Public Administration are at 180+. Plan for the Nigerian Law School year after LLB.",
        actions: [
          "Apply to LLB programmes at UNILAG, UI, ABU, or OAU — all highly respected",
          "Research postgraduate programmes in Public Policy (University of Lagos, ABU) for after graduation",
          "Identify 2 alternative paths: Political Science, Public Administration, or International Relations",
        ],
      },
      {
        phase: "University", timeframe: "Years 1–5",
        title: "Law degree + governance experience",
        description: "Enrol in Law (LLB) or Political Science. Participate in moot court, Model UN, and legal aid clinics. Intern at government agencies, NGOs, or law firms.",
        actions: [
          "Join Law Clinic and participate in at least one moot court competition per year",
          "Intern at a government ministry, legislature, NGO, or law firm every vacation",
          "Complete one online certification in Public Policy, Constitutional Law, or International Relations",
        ],
      },
      {
        phase: "Early Career", timeframe: "Post-graduation",
        title: "Law School, public service, or civil society",
        description: "Complete Bar exams if in Law. NYSC in a government or policy institution. Consider postgraduate study in Public Policy, International Law, or Governance.",
        actions: [
          "Complete the Nigerian Law School Bar examination (if LLB) and call to bar",
          "Apply for positions at INEC, EFCC, National Assembly, or a think-tank during NYSC",
          "Consider an LLM or MPA postgraduate degree — many are available fully funded abroad",
        ],
      },
    ],
    universities: [
      { name: "University of Lagos — Faculty of Law", country: "Nigeria", relevantFor: ["broad_law_governance"], examBodies: ["WAEC", "NECO"], programmes: ["Law (LLB)", "International Relations", "Political Science"], cutoffGuide: "220+ UTME for Law" },
      { name: "Ahmadu Bello University (ABU)", country: "Nigeria", relevantFor: ["broad_law_governance"], examBodies: ["WAEC", "NECO"], programmes: ["Law", "Political Science", "Public Administration"], cutoffGuide: "210+ UTME" },
      { name: "University of Ibadan", country: "Nigeria", relevantFor: ["broad_law_governance"], examBodies: ["WAEC", "NECO"], programmes: ["Law", "Political Science", "Public Administration"] },
      { name: "Nigerian Law School", country: "Nigeria", relevantFor: ["broad_law_governance"], examBodies: ["all"], programmes: ["Bar Part I & II"], cutoffGuide: "After LLB only" },
      { name: "Ghana School of Law", country: "Ghana", relevantFor: ["broad_law_governance"], examBodies: ["WAEC", "WASSCE"], programmes: ["Law (LLB)", "Professional Law Course"] },
      { name: "KNUST — Department of Political Science", country: "Ghana", relevantFor: ["broad_law_governance"], examBodies: ["WAEC", "WASSCE"], programmes: ["Political Science", "Development Studies"] },
    ],
  },
]

export function getRoadmapForPath(broadPathId: string): CareerRoadmap | null {
  return CAREER_ROADMAPS.find(r => r.broadPathId === broadPathId) ?? null
}

export function getUniversitiesForCareer(broadPathId: string, examBody?: string): UniversityRecommendation[] {
  const roadmap = getRoadmapForPath(broadPathId)
  if (!roadmap) return []
  if (!examBody) return roadmap.universities
  return roadmap.universities.filter(u => u.examBodies.includes("all") || u.examBodies.includes(examBody))
}
